// =============================================================================
// bibliothequesServies.js : les bibliothèques chargées à la demande viennent
// d'Aramis, jamais d'un CDN (briefing de sécurité du 27/10/2026, « ferme tout »)
// =============================================================================
// Avant : jsPDF (cdnjs), PptxGenJS (jsDelivr), docx (cdnjs, puis unpkg et
// jsDelivr en secours) et otpauth (jsDelivr, enrôlement 2FA de la Super
// administration) se chargeaient depuis des CDN tiers, sans contrôle
// d'intégrité. Un CDN compromis aurait exécuté son code dans Aramis, avec la
// session ouverte, et lu le secret TOTP au moment de l'enrôlement.
//
// Désormais :
//   • otpauth et docx viennent du paquet npm (bundle, import() paresseux,
//     jamais dans le bundle principal) ;
//   • jsPDF et PptxGenJS, chargés par leur fichier autonome (UMD), sont servis
//     par Aramis depuis public/vendor/, version dans le nom. Ce sont, octet
//     pour octet, les fichiers que servaient les CDN : empreintes recoupées le
//     27/10/2026 entre le paquet npm (vérifié par son intégrité au registre),
//     cdnjs (empreinte publiée) et jsDelivr. Chacun est chargé avec son
//     empreinte SRI (sha384) : un fichier modifié ne s'exécute pas.
//
// C'est la SEULE porte de src/ qui crée un <script> (verrou :
// src/bibliothequesServies.test.js, qui recalcule aussi chaque empreinte et lit
// la politique de sécurité de vercel.json).
//
// Changer de version : déposer le nouveau fichier sous son nouveau nom dans
// public/vendor/, écrire ici son chemin et son empreinte
// (`openssl dgst -sha384 -binary <fichier> | base64`), retirer l'ancien.
// =============================================================================

export const BIBLIOTHEQUES = Object.freeze({
  jspdf: Object.freeze({
    src: "/vendor/jspdf-2.5.1.umd.min.js",
    integrity: "sha384-JcnsjUPPylna1s1fvi1u12X5qjY5OL56iySh75FdtrwhO/SWXgMjoVqcKyIIWOLk",
    lire: () => (typeof window !== "undefined" && window.jspdf && window.jspdf.jsPDF) || null,
  }),
  pptxgenjs: Object.freeze({
    src: "/vendor/pptxgenjs-3.12.0.bundle.js",
    integrity: "sha384-Cck14aA9cifjYolcnjebXRfWGkz5ltHMBiG4px/j8GS+xQcb7OhNQWZYyWjQ+UwQ",
    lire: () => (typeof window !== "undefined" && window.PptxGenJS) || null,
  }),
});

const enCours = new Map();

/**
 * Charge une bibliothèque servie par Aramis (une seule fois par page) et rend
 * ce qu'elle expose. Un échec (réseau, empreinte refusée) se laisse retenter.
 */
export function chargerBibliotheque(nom) {
  const b = BIBLIOTHEQUES[nom];
  if (!b) return Promise.reject(new Error(`Bibliothèque inconnue : ${nom}`));
  const deja = b.lire();
  if (deja) return Promise.resolve(deja);
  if (enCours.has(nom)) return enCours.get(nom);
  const promesse = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = b.src;
    s.integrity = b.integrity;
    s.crossOrigin = "anonymous";
    s.async = true;
    const echec = (message) => {
      enCours.delete(nom);
      s.remove();
      reject(new Error(message));
    };
    s.onload = () => {
      const v = b.lire();
      if (!v) { echec(`${nom} chargé mais introuvable`); return; }
      enCours.delete(nom); // désormais servi par b.lire()
      resolve(v);
    };
    s.onerror = () => echec(`Impossible de charger ${nom}`);
    document.head.appendChild(s);
  });
  enCours.set(nom, promesse);
  return promesse;
}

/** Le constructeur jsPDF. */
export const chargerJsPDF = () => chargerBibliotheque("jspdf");

/** Le constructeur PptxGenJS. */
export const chargerPptxGenJS = () => chargerBibliotheque("pptxgenjs");
